/**
 * Net-cost calc module for the "bikPlusNetImpact" mode (BCH).
 *
 * Calc modules import ratebook.js for shared primitives; this is safe because
 * cross-references only happen inside function bodies, never at module-evaluation time.
 *
 * Both limits come from the quote's Jaama driver grade (quote.driverGrade.deloitteFilters):
 *   - WLC limit      = companyCostTotalTo (monthly £, same field the default mode uses for Remaining WLC)
 *   - Max trade-up   = employeeCostSalarySacrificeTo (monthly £)
 *
 * Trade-up amount (gross salary sacrifice) = monthly WLC - grade WLC limit, only when WLC exceeds the limit.
 *
 * Rounding: all intermediate maths runs at full precision; every returned row is floored to the
 * penny as the final step (client instruction, scoped to this calc only).
 */

import Ratebook from '@/helpers/ratebook';
import { floorToPennies } from '@/helpers/utils';
import { niRateByTaxBand } from './bik-plus-net-impact-config';

export const CALC_NAME = 'bikPlusNetImpact';

/**
 * Returns true when the BCH product has the bikPlusNetImpact net-cost display mode enabled.
 *
 * @param {object} adminPortalProduct
 * @returns {boolean}
 */
export function isEnabled(adminPortalProduct) {
    return adminPortalProduct?.calcs_net_cost_display === CALC_NAME;
}

/**
 * Reads a numeric limit from the driver grade's deloitteFilters.
 * Missing, null, non-numeric or zero values resolve to null (unset), matching how the
 * existing wholeLifeCostRemaining() treats companyCostTotalTo.
 *
 * @param {object|null} driverGrade
 * @param {string} field
 * @returns {number|null}
 */
function gradeLimit(driverGrade, field) {
    const raw = driverGrade?.deloitteFilters?.[field];
    const value = Number(raw);
    return raw !== null && raw !== undefined && raw !== '' && !isNaN(value) && value !== 0 ? value : null;
}

/**
 * Returns the monthly WLC limit (£) for the driver grade, or null when unset.
 *
 * @param {object|null} driverGrade
 * @returns {number|null}
 */
export function wlcLimit(driverGrade) {
    return gradeLimit(driverGrade, 'companyCostTotalTo');
}

/**
 * Returns the maximum permitted monthly salary sacrifice trade-up (£) for the driver grade, or null when unset.
 *
 * @param {object|null} driverGrade
 * @returns {number|null}
 */
export function maxTradeup(driverGrade) {
    return gradeLimit(driverGrade, 'employeeCostSalarySacrificeTo');
}

/**
 * Returns true when the quote's whole-life cost exceeds the grade's WLC limit.
 * Returns false (not triggered) when the limit cannot be resolved or WLC is unavailable.
 *
 * @param {object} ratebook
 * @param {number} term
 * @param {object|null} driverGrade
 * @returns {boolean}
 */
export function isTradeupTriggered(ratebook, term, driverGrade) {
    const wlc = Ratebook.wholeLifeCostRaw(ratebook, term);
    const limit = wlcLimit(driverGrade);
    return limit !== null && wlc !== null && wlc > limit;
}

/**
 * Returns the unrounded monthly salary sacrifice trade-up (£): the amount by which the quote's
 * WLC exceeds the grade's WLC limit, or 0 when not triggered.
 *
 * @param {object} ratebook
 * @param {number} term
 * @param {object|null} driverGrade
 * @returns {number}
 */
export function tradeupAmount(ratebook, term, driverGrade) {
    if (!isTradeupTriggered(ratebook, term, driverGrade)) { return 0; }
    return Ratebook.wholeLifeCostRaw(ratebook, term) - wlcLimit(driverGrade);
}

/**
 * Builds the full breakdown object for the bikPlusNetImpact mode.
 * All row values are £0 when the tradeup is not triggered.
 * Remaining WLC is 0 when triggered (the trade-up consumes the excess).
 * Every monetary row is floored to the penny; wlcLimit/maxTradeup are returned as read from the grade.
 *
 * @param {object} ratebook
 * @param {number} term
 * @param {number} ratePercent - e.g. 20, 40, or 45
 * @param {object|null} driverGrade - quote.driverGrade (Jaama grade object with deloitteFilters)
 * @returns {{
 *   averageMonthlyBik: number,
 *   salarySacrifice: number,
 *   incomeTaxSaved: number,
 *   nationalInsuranceSaved: number,
 *   netImpact: number,
 *   employeeNetCost: number,
 *   wlcRemaining: number,
 *   wlcLimit: number|null,
 *   maxTradeup: number|null,
 *   tradeupTriggered: boolean,
 *   exceedsMaxTradeup: boolean,
 * }}
 */
export function breakdown(ratebook, term, ratePercent, driverGrade) {
    const averageMonthlyBik = Ratebook.averageMonthlyBikRaw(ratebook, term) ?? 0;
    const limit = wlcLimit(driverGrade);
    const max = maxTradeup(driverGrade);
    const triggered = isTradeupTriggered(ratebook, term, driverGrade);

    const salarySacrifice = tradeupAmount(ratebook, term, driverGrade);
    const rate = Number(ratePercent) || 0;
    const niRate = niRateByTaxBand[rate] ?? 0;
    const incomeTaxSaved = salarySacrifice * (rate / 100);
    const nationalInsuranceSaved = salarySacrifice * niRate;
    const netImpact = salarySacrifice - incomeTaxSaved - nationalInsuranceSaved;
    const employeeNetCost = netImpact + averageMonthlyBik;

    // Remaining WLC is 0 when triggered (the trade-up consumes the excess).
    let wlcRemaining = 0;
    if (!triggered && limit !== null) {
        wlcRemaining = limit - (Ratebook.wholeLifeCostRaw(ratebook, term) ?? 0);
    }

    return {
        averageMonthlyBik: floorToPennies(averageMonthlyBik),
        salarySacrifice: floorToPennies(salarySacrifice),
        incomeTaxSaved: floorToPennies(incomeTaxSaved),
        nationalInsuranceSaved: floorToPennies(nationalInsuranceSaved),
        netImpact: floorToPennies(netImpact),
        employeeNetCost: floorToPennies(employeeNetCost),
        wlcRemaining: floorToPennies(wlcRemaining),
        wlcLimit: limit,
        maxTradeup: max,
        tradeupTriggered: triggered,
        // No max on the grade means nothing to exceed - never block.
        exceedsMaxTradeup: max !== null && salarySacrifice > max,
    };
}

/**
 * Returns the employee net cost for use in the shared netCost() dispatcher.
 *
 * @param {object} ratebook
 * @param {number} term
 * @param {{ ratePercent?: number, driverGrade?: object }} [context={}]
 * @returns {number}
 */
export function netCost(ratebook, term, context = {}) {
    return breakdown(ratebook, term, context.ratePercent, context.driverGrade).employeeNetCost;
}

export default { CALC_NAME, isEnabled, wlcLimit, maxTradeup, isTradeupTriggered, tradeupAmount, breakdown, netCost };
